// components/Header.js
"use client";
import Link from "next/link";
import { useEffect, useState } from "react";
import React from "react";
import logo from "../public/images/logo.webp";
import Image from "next/image";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Routes } from "@/config/routes";

const NAV_ITEMS = [
  { label: "Services", type: "scroll", target: "services" },
  { label: "BMR/TDEE Calculator", href: Routes.publicCalculator },
  { label: "About", href: Routes.aboutus },
  { label: "Success Stories", href: Routes.successstories },
  { label: "Blog", href: Routes.blog },
  { label: "Contact", href: Routes.contactus },
];

const HomePageHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const handleClick = () => {
    if (pathname === "/") {
      // Already on homepage → scroll smoothly
      const topElement = document.getElementById("top");
      if (topElement) {
        topElement.scrollIntoView({ behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } else {
      // Not on homepage → navigate to "/"
      router.push("/");
    }
  };

  useEffect(() => {
    const sectionId = searchParams.get("scroll");
    if (sectionId) {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [searchParams]);

  const scrollToSection = (id) => {
    if (pathname === "/") {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
      } else {
        console.warn("Element not found with id:", id);
      }
    } else {
      router.push(`/?scroll=${id}`);
    }
  };

  const isActive = (item) => {
    if (!item.href) return false;
    const itemPath = item.href.split("?")[0];
    if (itemPath === "/public-calculator") {
      return pathname === "/public-calculator";
    }
    return pathname === item.href;
  };

  return (
    <header className="bg-white shadow-sm flex w-full z-50 px-4 sm:px-6">
      <div className="max-w-[1400px] mx-auto w-full">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <button
              onClick={handleClick}
              className="flex items-center focus:outline-none"
            >
              <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center mr-2 relative overflow-hidden">
                <Image
                  src={logo}
                  alt="Logo"
                  width={56}
                  height={56}
                  className="rounded-full object-fill"
                />
              </div>
              <span className="text-lg sm:text-xl font-semibold text-green-700">
                Macros & Meals
              </span>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden [@media(min-width:870px)]:flex items-center">
            <div className="flex items-center space-x-0.5 lg:space-x-1">
              {NAV_ITEMS.map((item) => {
                const active = isActive(item);
                const className = `text-sm lg:text-base transition-colors px-2.5 lg:px-3.5 py-2 whitespace-nowrap ${
                  active
                    ? "text-green-600 font-semibold"
                    : "text-gray-700 hover:text-green-600"
                }`;

                if (item.type === "scroll") {
                  return (
                    <button
                      key={item.label}
                      onClick={() => scrollToSection(item.target)}
                      className={className}
                    >
                      {item.label}
                    </button>
                  );
                }

                return (
                  <Link key={item.label} href={item.href} className={className}>
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Desktop Auth Buttons */}
          <div className="hidden [@media(min-width:870px)]:flex items-center pr-5">
            <Link
              href={Routes.login}
              className="text-sm md:text-base text-gray-700 hover:text-green-600 transition-colors px-3 lg:px-4 py-2 whitespace-nowrap"
            >
              Login
            </Link>
            <Link
              href={Routes.register}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors ml-2 whitespace-nowrap"
            >
              Get Started
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="[@media(min-width:870px)]:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-green-600 focus:outline-none p-2"
            >
              <div className="w-6 h-6 flex flex-col justify-center">
                {isMenuOpen ? (
                  <div className="relative">
                    <div className="w-6 h-0.5 bg-current transform rotate-45 absolute"></div>
                    <div className="w-6 h-0.5 bg-current transform -rotate-45 absolute"></div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className="w-6 h-0.5 bg-current mb-1"></div>
                    <div className="w-6 h-0.5 bg-current mb-1"></div>
                    <div className="w-6 h-0.5 bg-current"></div>
                  </div>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="[@media(min-width:870px)]:hidden border-t py-2">
            <div className="flex flex-col space-y-1">
              {NAV_ITEMS.map((item) => {
                const active = isActive(item);
                const className = `px-4 py-2.5 text-left text-sm sm:text-base transition-colors block w-full ${
                  active
                    ? "text-green-600 font-semibold"
                    : "text-gray-700 hover:text-green-600"
                }`;

                if (item.type === "scroll") {
                  return (
                    <button
                      key={item.label}
                      onClick={() => {
                        setIsMenuOpen(false);
                        scrollToSection(item.target);
                      }}
                      className={className}
                    >
                      {item.label}
                    </button>
                  );
                }

                return (
                  <Link
                    key={item.label}
                    href={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={className}
                  >
                    {item.label}
                  </Link>
                );
              })}
              <Link
                href={Routes.login}
                onClick={() => setIsMenuOpen(false)}
                className="text-gray-700 hover:text-green-600 px-4 py-2.5 text-sm sm:text-base"
              >
                Login
              </Link>
              <Link
                href={Routes.register}
                onClick={() => setIsMenuOpen(false)}
                className="bg-green-600 text-white rounded-md hover:bg-green-700 mx-4 py-2.5 text-center mt-2 font-medium text-sm sm:text-base"
              >
                Get Started
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default HomePageHeader;
